import React, { useState, useEffect, type FC } from 'react';
import SearchDropdown from '../common/SearchDropdown';
import RadioButtonRowSelect from '../common/RadioButtonRowSelect';
import { PropertyMapperProps } from '../../utility/types';

const STATIC_VALUE_PREFIX = '__STATIC__';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC'
];

const isStaticValue = (value: string | undefined) => typeof value === 'string' && value.startsWith(STATIC_VALUE_PREFIX);
const stripStaticPrefix = (value: string) => value.slice(STATIC_VALUE_PREFIX.length);

const PropertyMapper: FC<PropertyMapperProps> = ({ csvHeaders, existingMapping, mappableFieldsJson, formId }) => {
  const [ existingMappingObj, _setExistingMapping ] = useState(JSON.parse(existingMapping));
  const [ mappingObject, setMappingObject ] = useState(existingMappingObj);
  const [ csvHeaderObject, _setCSV ] = useState(JSON.parse(csvHeaders));
  const mappableFields = JSON.parse(mappableFieldsJson);
  const addressTypes = [ 'Seperate', 'Full Street', 'Complete Address' ];

  const setSelectedAddressType = () => {
    if (existingMappingObj.street) {
      return 'Full Street';
    }
    if (existingMappingObj.complete_address) {
      return 'Complete Address';
    }
    return 'Seperate';
  };

  const [ addressType, setAddressType ] = useState(setSelectedAddressType());
  const seperateSkippableKeys = [ 'complete_address', 'street' ];
  const fullStreetSkippableKeys = [ 'complete_address', 'street_number', 'direction', 'street_name', 'unit_number' ];
  const completeAddressSkippableKeys = [ 'street', 'street_number', 'direction', 'street_name', 'unit_number', 'street_line_two', 'city', 'state', 'zipcode', 'county', 'country' ];

  const filteredMappableKeys = Object.keys(mappableFields).filter((key) => {
    if (addressType == 'Seperate') {
      return !seperateSkippableKeys.includes(key);
    }
    if (addressType == 'Complete Address') {
      return !completeAddressSkippableKeys.includes(key);
    }
    if (addressType == 'Full Street') {
      return !fullStreetSkippableKeys.includes(key);
    }
  });

  const buildInitialStaticFields = () => {
    const initial = {};
    Object.keys(mappableFields).forEach(key => {
      initial[key] = isStaticValue(existingMappingObj[key]);
    });
    return initial;
  };

  const [ staticFields, setStaticFields ] = useState(buildInitialStaticFields());

  const updateMapping = (mapping) => {
    const filteredMapping = {};
    filteredMappableKeys.forEach(key => {
      filteredMapping[key] = mapping[key];
    });
    const hiddenInput = document.getElementById(formId) as HTMLInputElement;
    hiddenInput.value = JSON.stringify(filteredMapping);
  };

  useEffect(() => { updateMapping(mappingObject); }, [ mappingObject ]);

  const setFieldValue = (key: string, value: string) => {
    setMappingObject(mappingObject => {
      const updated = { ...mappingObject };
      if (value === '') {
        delete updated[key];
      } else {
        updated[key] = value;
      }
      return updated;
    });
  };

  const toggleStatic = (key: string) => {
    setFieldValue(key, '');
    setStaticFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <div className='flex my-6 justify-center'>
        <RadioButtonRowSelect
          title='Address Type'
          subtitle='Select the type of address used in your csv'
          options={addressTypes}
          selected={addressType}
          onSelect={(selectedType) => {
            setAddressType(selectedType);
          }}
        />
      </div>
      {filteredMappableKeys.map(key =>
        <React.Fragment key={key}>
          { key === 'bedrooms' &&
            <div className='border-t border-gray-300 mt-10 pt-5 pb-3'>
              <h3 className='block text-sm font-medium leading-6 text-gray-900'>Property Details</h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">Add property detail mappings for non mls properties</p>
            </div>
          }
          { key === 'auction_number' &&
            <div className='border-t border-gray-300 mt-10 pt-5 pb-3'>
              <h3 className='block text-sm font-medium leading-6 text-gray-900'>Other Property Details</h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">Add property details specific to this list</p>
            </div>
          }
          <div className='flex justify-between items-baseline gap-x-3'>
            <h3 className='block text-sm font-medium leading-6 text-gray-900 whitespace-nowrap'>{mappableFields[key]}</h3>
            <div className='flex items-center gap-x-2 flex-1 justify-end'>
              {staticFields[key] ? (
                key === 'state' ? (
                  <select
                    value={isStaticValue(mappingObject[key]) ? stripStaticPrefix(mappingObject[key]) : ''}
                    onChange={(e) => setFieldValue(key, e.target.value ? `${STATIC_VALUE_PREFIX}${e.target.value}` : '')}
                    className='rounded-md border-gray-300 text-sm'
                  >
                    <option value=''>Select a state</option>
                    {US_STATES.map(state => <option key={state} value={state}>{state}</option>)}
                  </select>
                ) : (
                  <input
                    type='text'
                    value={isStaticValue(mappingObject[key]) ? stripStaticPrefix(mappingObject[key]) : ''}
                    onChange={(e) => setFieldValue(key, e.target.value ? `${STATIC_VALUE_PREFIX}${e.target.value}` : '')}
                    placeholder='Enter a fixed value for every row'
                    className='rounded-md border-gray-300 text-sm px-2 py-1'
                  />
                )
              ) : (
                <SearchDropdown initialValue={existingMappingObj[key]} selections={Object.keys(csvHeaderObject).map(headerKey => ({ key: headerKey, subtitle: csvHeaderObject[headerKey] }))} onSelect={(selected: string) => {
                  if (selected || selected === '') {
                    setFieldValue(key, selected);
                  }
                }} />
              )}
              <button
                type='button'
                onClick={() => toggleStatic(key)}
                className='text-xs text-indigo-600 hover:text-indigo-900 whitespace-nowrap'
              >
                {staticFields[key] ? 'Use CSV column' : 'Use fixed value'}
              </button>
            </div>
          </div>
        </React.Fragment>
      )}
    </>
  );
};

export default PropertyMapper;
